import React from 'react';
import { motion } from 'framer-motion';
import { 
  ClipboardCheck, Plus, Check, X, AlertTriangle, AlertCircle, PenTool
} from 'lucide-react';
import { Button } from '../ui/button';

const TimeRecordsTab = ({
  pendingTimeRecords,
  setShowManualTimeModal,
  handleApproveTimeRecord,
  handleRejectTimeRecord,
  formatDateTime
}) => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Time Records</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-[var(--text-muted)]">
            {pendingTimeRecords.length} pending approval
          </div>
          <Button 
            onClick={() => setShowManualTimeModal(true)} 
            className="btn-primary"
            data-testid="manual-time-entry-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Manual Entry
          </Button>
        </div>
      </div>
      
      {pendingTimeRecords.length === 0 ? (
        <div className="card p-12 text-center">
          <ClipboardCheck className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Pending Approvals</h3>
          <p className="text-[var(--text-secondary)]">
            All worker time records have been reviewed.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingTimeRecords.map(record => (
            <motion.div 
              key={record.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card overflow-hidden"
              data-testid={`time-record-${record.id}`}
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{record.worker_name}</h3>
                      <span className="badge-warning">Pending Approval</span>
                      {record.is_self_signed && (
                        <span className="badge-error flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Self-Signed
                        </span>
                      )}
                    </div>
                    <p className="text-[var(--text-secondary)]">{record.service_name}</p>
                    <p className="text-sm text-[var(--text-muted)]">
                      Customer: {record.customer_name}
                    </p>
                    
                    <div className="mt-4 grid md:grid-cols-3 gap-4">
                      <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)]">Clock In</p>
                        <p className="font-semibold">{formatDateTime(record.clock_in_time)}</p>
                      </div>
                      <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)]">Clock Out</p>
                        <p className="font-semibold">{formatDateTime(record.clock_out_time)}</p>
                      </div>
                      <div className="p-3 bg-[var(--accent)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)]">Hours Worked</p>
                        <p className="font-bold text-[var(--primary)] text-xl">{record.hours_worked}h</p>
                      </div>
                    </div>
                    
                    {/* Signature Info */}
                    <div className="mt-4 p-4 bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <PenTool className="w-4 h-4 text-[var(--primary)]" />
                        <span className="font-semibold">Signature</span>
                      </div>
                      <div className="flex items-start gap-4">
                        {record.signature_data && (
                          <img 
                            src={record.signature_data} 
                            alt="Signature" 
                            className="h-16 border rounded bg-[var(--background-subtle)]"
                          />
                        )}
                        <div>
                          <p className="text-sm">
                            <span className="text-[var(--text-muted)]">Signed by:</span>{' '}
                            <span className="font-medium">{record.signature_name}</span>
                          </p>
                          {record.is_self_signed && (
                            <div className="mt-1 p-2 bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded text-sm">
                              <p className="text-amber-800 font-medium">Self-signed - Client not available</p>
                              <p className="text-amber-600 text-xs">Reason: {record.self_sign_reason || 'Not provided'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button 
                      onClick={() => handleApproveTimeRecord(record.id)}
                      className="bg-[var(--success)] hover:bg-[var(--success)]/90 text-white"
                      data-testid={`approve-time-${record.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleRejectTimeRecord(record.id)}
                      data-testid={`reject-time-${record.id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      
      {/* Info Box */}
      <div className="mt-6 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-800">Payroll Processing</p>
            <p className="text-[#D4AF37]">
              Only approved time records will be included in payroll generation.
              Self-signed records should be verified before approval.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TimeRecordsTab;
